import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  ShieldCheck,
  MapPin,
  Sparkles,
  HeartHandshake,
  Building2,
  HomeIcon,
  ClipboardCheck,
  Star,
  ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import useHomepageImages from "@/lib/useHomepageImages";
import CmsImage from "@/components/CmsImage";
import { TIERS, IMAGES, PRICING_DISCLAIMER } from "@/data/content";
import { useBusiness, usePublicSettings } from "@/contexts/SettingsContext";
import { usePublicServices, iconForService } from "@/contexts/ServicesContext";

const trustPoints = [
  { icon: MapPin, label: "Sacramento-area cleaning service" },
  { icon: ClipboardCheck, label: "Per-job estimates, not hourly" },
  { icon: HomeIcon, label: "Residential and commercial cleaning" },
  { icon: Sparkles, label: "Move-in and move-out specialists" },
  { icon: HeartHandshake, label: "Great for landlords, PMs, realtors & business owners" },
  { icon: ShieldCheck, label: "Easy estimate request process" },
];

export default function Home() {
  const BUSINESS = useBusiness();
  const settings = usePublicSettings();
  const { services } = usePublicServices();
  const { pick: pickImage } = useHomepageImages();
  const heroImg = pickImage("residential_1", IMAGES.heroResidential, "Bright, clean residential interior");
  const resImg = pickImage("residential_2", IMAGES.residentialService, "Clean residential living room");
  const comImg = pickImage("commercial_1", IMAGES.office, "Modern commercial office space");
  useSEO({ slug: "home", title: "Sacramento Move-In, Move-Out & Commercial Cleaning | P & A Cleaning" });

  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get("/public/reviews", { params: { limit: 3 } }).then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="absolute inset-0 bg-dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="reveal">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
                <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Sacramento, CA
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold font-display text-[#0F172A] leading-[1.05] tracking-tight">
                Move-In, Move-Out &{" "}
                <span className="gradient-text">Commercial Cleaning</span> in Sacramento
              </h1>
              <p className="mt-6 text-lg text-[#475569] leading-relaxed max-w-xl">
                Reliable per-job cleaning estimates for move-outs, pre-listings,
                recurring home cleaning, office cleaning, junk removal, carpet
                cleaning, and yard cleanup.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="hero-cta-estimate"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors shadow-soft"
                >
                  Request an Estimate <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/commercial-cleaning"
                  data-testid="hero-cta-commercial"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  Commercial Cleaning Quote
                </Link>
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="hero-cta-call"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call / Text Now
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#475569]">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#34D399]" /> Per-job pricing</span>
                <span className="inline-flex items-center gap-1.5"><HomeIcon className="w-4 h-4 text-[#A78BFA]" /> Residential</span>
                <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#1E3A8A]" /> Commercial</span>
              </div>
            </div>

            <div className="relative reveal">
              <div className="relative rounded-3xl overflow-hidden shadow-card border border-white">
                <CmsImage
                  src={heroImg.src}
                  alt={heroImg.alt}
                  loading={heroImg.loading}
                  testId="home-hero-image"
                  className="w-full h-[420px] lg:h-[520px] object-cover"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-card p-5 border border-slate-100 max-w-[240px]">
                <p className="text-xs font-semibold text-[#047857] uppercase tracking-wider">
                  Family business
                </p>
                <p className="mt-1 font-display font-semibold text-[#0F172A]">
                  Friendly, reliable, locally based in Sacramento.
                </p>
              </div>
              <div className="hidden md:block absolute -top-4 -right-4 bg-[#1E3A8A] text-white rounded-2xl shadow-card p-4 max-w-[200px]">
                <p className="text-xs font-semibold uppercase tracking-wider opacity-80">
                  Specialty
                </p>
                <p className="mt-1 font-display font-semibold leading-snug">
                  Move-outs, pre-listings & turnovers
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SPLIT FEATURE */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
              Two sides, one trusted team
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Residential & Commercial Cleaning Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <div
              data-testid="feature-card-residential"
              className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-soft hover:shadow-card transition-shadow"
            >
              <CmsImage
                src={resImg.src}
                alt={resImg.alt}
                loading={resImg.loading}
                testId="home-residential-card-image"
                className="w-full h-56 object-cover"
              />
              <div className="p-8">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/5 text-[#1E3A8A] text-xs font-semibold">
                  <HomeIcon className="w-3.5 h-3.5" /> Residential
                </span>
                <h3 className="mt-4 text-2xl font-display font-semibold text-[#0F172A]">
                  Residential Cleaning
                </h3>
                <p className="mt-3 text-[#475569] leading-relaxed">
                  Move-in, move-out, pre-listing, recurring home cleaning,
                  junk removal, carpet cleaning, and yard cleanup.
                </p>
                <Link
                  to="/services"
                  data-testid="feature-residential-cta"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors"
                >
                  View Residential Services <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <div
              data-testid="feature-card-commercial"
              className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-soft hover:shadow-card transition-shadow"
            >
              <CmsImage
                src={comImg.src}
                alt={comImg.alt}
                loading={comImg.loading}
                testId="home-commercial-card-image"
                className="w-full h-56 object-cover"
              />
              <div className="p-8">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#A78BFA]/10 text-[#7C3AED] text-xs font-semibold">
                  <Building2 className="w-3.5 h-3.5" /> Commercial
                </span>
                <h3 className="mt-4 text-2xl font-display font-semibold text-[#0F172A]">
                  Commercial Cleaning
                </h3>
                <p className="mt-3 text-[#475569] leading-relaxed">
                  Office cleaning, business cleaning, restrooms, breakrooms,
                  common areas, and recurring cleaning plans.
                </p>
                <Link
                  to="/commercial-cleaning"
                  data-testid="feature-commercial-cta"
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#7C3AED] text-white font-semibold hover:bg-[#6D28D9] transition-colors"
                >
                  Get Commercial Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR SERVICES */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#047857]">
                What we do
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
                Popular Services
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-see-all"
              className="text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF] inline-flex items-center gap-1"
            >
              See all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.slice(0, 6).map((s) => {
              const Icon = iconForService(s);
              return (
                <div
                  key={s.id || s.slug}
                  data-testid={`service-card-${s.slug}`}
                  className="bg-white rounded-2xl p-6 border border-slate-100 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E3A8A]/10 to-[#A78BFA]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#1E3A8A]" strokeWidth={2} />
                    </div>
                    <h3 className="text-lg font-display font-semibold text-[#0F172A] leading-tight">
                      {s.learn_more_url ? (
                        <Link
                          to={s.learn_more_url}
                          data-testid={`service-card-${s.slug}-title-link`}
                          className="hover:text-[#1E3A8A] transition-colors"
                        >
                          {s.name}
                        </Link>
                      ) : (
                        s.name
                      )}
                    </h3>
                  </div>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    {s.short_description}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    {s.learn_more_url && (
                      <Link
                        to={s.learn_more_url}
                        data-testid={`service-card-${s.slug}-learnmore`}
                        aria-label={`Learn more about ${s.name}`}
                        className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full border border-[#1E3A8A]/30 text-sm font-semibold text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition-colors"
                      >
                        Learn More <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                      </Link>
                    )}
                    <Link
                      to={s.estimate_cta_url || "/request-estimate"}
                      data-testid={`service-card-${s.slug}-cta`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF]"
                    >
                      Request Estimate <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
              Ballpark pricing
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Simple, transparent estimates
            </h2>
            <p className="mt-4 text-[#475569]">
              {(settings.pricing_disclaimer || PRICING_DISCLAIMER).split(".")[0]}. Final pricing is per job, not
              per hour, and may change after inspection or review of photos.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <div
                key={t.name}
                data-testid={`pricing-preview-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative rounded-2xl p-7 border transition-all ${
                  t.popular
                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A] shadow-card"
                    : "bg-white text-[#0F172A] border-slate-100 shadow-soft"
                }`}
              >
                {t.popular && (
                  <span className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-semibold tracking-wider uppercase bg-[#34D399] text-[#0F172A]">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-display font-semibold text-xl ${t.popular ? "text-white" : "text-[#0F172A]"}`}>
                  {t.name}
                </h3>
                <p className={`mt-2 text-sm ${t.popular ? "text-white/80" : "text-[#475569]"}`}>
                  {t.tagline}
                </p>
                <ul className={`mt-5 space-y-2 text-sm ${t.popular ? "text-white/90" : "text-[#475569]"}`}>
                  {t.bullets.slice(0, 3).map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.popular ? "bg-[#34D399]" : "bg-[#1E3A8A]"}`} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/pricing"
              data-testid="pricing-preview-cta-full"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors"
            >
              See full pricing
            </Link>
            <Link
              to="/request-estimate"
              data-testid="pricing-preview-cta-estimate"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#1E3A8A] font-semibold border border-slate-200 hover:bg-slate-50 transition-colors"
            >
              Request an Estimate <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#1E3A8A]">
              Why choose us
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              Local, reliable, business-friendly
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {trustPoints.map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="bg-white rounded-2xl p-6 border border-slate-100 flex items-start gap-4"
              >
                <span className="w-11 h-11 rounded-xl bg-[#34D399]/15 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#10B981]" />
                </span>
                <p className="font-medium text-[#0F172A] leading-snug">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YELP REVIEWS PLACEHOLDER */}
      <section className="py-16 lg:py-24 bg-white" data-testid="yelp-reviews-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#7C3AED]">
              Reviews
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold text-[#0F172A]">
              What Customers Are Saying
            </h2>
          </div>

          <div className="rounded-3xl border border-slate-100 bg-[#F8FAFC] p-8 lg:p-12 shadow-soft">
            {reviews.length > 0 ? (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {reviews.map((rv) => (
                    <div key={rv.id} data-testid={`review-card-${rv.id}`} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-soft text-left">
                      <div className="flex items-center gap-1 text-[#F59E0B]">
                        {Array.from({ length: Math.max(1, Math.min(5, rv.rating || 5)) }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-current" />
                        ))}
                      </div>
                      <p className="mt-3 text-[#0F172A] leading-relaxed text-sm">{rv.review_excerpt}</p>
                      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#475569]">
                        {rv.customer_display_name} <span className="text-slate-500">· {rv.source}</span>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <a
                    href="https://m.yelp.com/biz/paulas-cleaning-service-arden-arcade"
                    target="_blank" rel="noreferrer"
                    data-testid="yelp-reviews-cta"
                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D32323] text-white font-semibold hover:bg-[#B91C1C] transition-colors shadow-soft"
                  >
                    View More Reviews on Yelp <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center gap-1 text-[#F59E0B]" aria-hidden="true">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="w-6 h-6 fill-current" />
                  ))}
                </div>
                <p className="mt-5 text-lg text-[#0F172A] font-display font-medium max-w-2xl leading-relaxed">
                  Customer reviews will be added soon. Visit our Yelp page to see
                  current customer reviews.
                </p>
                <a
                  href="https://m.yelp.com/biz/paulas-cleaning-service-arden-arcade"
                  target="_blank" rel="noreferrer"
                  data-testid="yelp-reviews-cta"
                  className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#D32323] text-white font-semibold hover:bg-[#B91C1C] transition-colors shadow-soft"
                >
                  View More Reviews on Yelp <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E3A8A] via-[#3B3B98] to-[#8B5CF6] p-10 lg:p-16 text-white">
            <div className="absolute inset-0 bg-dot-grid opacity-20 pointer-events-none" />
            <div className="relative max-w-3xl">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold leading-tight">
                Ready for a cleaner home, office, or property?
              </h2>
              <p className="mt-4 text-white/85 text-lg max-w-2xl">
                Tell us a little about the space and we'll get back with a
                per-job estimate and availability.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/request-estimate"
                  data-testid="final-cta-estimate"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 transition-colors"
                >
                  Request an Estimate <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/commercial-cleaning"
                  data-testid="final-cta-commercial"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#34D399] text-[#0F172A] font-semibold hover:bg-[#10B981] transition-colors"
                >
                  Get Commercial Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
